import type { MenuProps } from 'antd';
import { useChatContext } from '@/contexts/chat/context';
import { useRefs } from '@/scripts/hooks';
import { useSessionContext } from '@/contexts/session/context';
import { MediaType } from '@/contexts/chat/scripts/hooks/useQuickVO/types';
import type { User } from '@/configs/apis/user';
import useDragMove from '../../../hooks/useDragMove';
import CallTimer from '../callOperate/components/callTimer';
import type { CallMemberItem } from '../..';
import styles from './index.module.scss';
import FixedIcon from './images/fixed.svg?react';
import MoreIcon from './images/more.svg?react';
import UnMuteIcon from './images/un_mute.svg?react';
import CallIcon from './images/call-icon.svg?react';
import ScreenIcon from './images/screen.svg?react';
import CardMoreOperate from './components/cardMoreOperate';
import DrawSpeak from './components/drawSpeak';
import { CardMoreOperateKey } from './components/cardMoreOperate/type';

const CallCard = ({
  member,
  user,
  mediaModalZoomIn,
  onFixed,
  isOnlyOne = false,
}: {
  member: CallMemberItem;
  user: User;
  mediaModalZoomIn: boolean;
  onFixed: () => void;
  isOnlyOne?: boolean;
}) => {
  const {
    chatMediaCall,
    chatMediaQuickVO,
    mediaIsPutThrough,
    mediaIsReady,
    mediaStartTime,
  } = useChatContext();
  const { session } = useSessionContext();
  const { roomUsers, roomUsersStreams, updateUserStreams, setVolume } =
    chatMediaQuickVO;

  const isSelf = user?.userId === member.id;

  const isSelfUnFixed = isSelf && !member.fixed;

  const { dragRef, style } = useDragMove({
    disabled: !(isSelfUnFixed && chatMediaCall.mediaModalZoomIn) || isOnlyOne,
    marginBottom: 76,
    thresholdBottom: 76,
    showLabelLeft: false,
    showLabelRight: false,
  });

  const videoRef = useRef<HTMLVideoElement | null>(null);
  // const screenVideoRef = useRef<HTMLVideoElement | null>(null);
  // const audioRef = useRef<HTMLAudioElement | null>(null);
  const refs = useRefs({ updateUserStreams });

  const ringing = useMemo(() => !mediaIsPutThrough, [mediaIsPutThrough]);

  const callUserInfo = useMemo(
    () => roomUsers?.find((roomUser) => roomUser.id === member.userId),
    [member.userId, roomUsers]
  );

  const callUserStream = useMemo(
    () => roomUsersStreams?.find((roomUser) => roomUser.userId === member.userId),
    [member.userId, roomUsersStreams]
  );

  const handleClickMore: MenuProps['onClick'] = useCallback(
    ({ key }: { key: string }) => {
      switch (key) {
        case CardMoreOperateKey.MUTE:
          setVolume(callUserInfo!.id, MediaType.MICROPHONECAMERA_AUDIO, 0);
          break;
        case CardMoreOperateKey.WATCH:
          break;
        case CardMoreOperateKey.PIN:
          onFixed();
          break;
        case CardMoreOperateKey.FULL_SCREEN:
          dragRef.current?.requestFullscreen();
          break;
        default:
          break;
      }
    },
    [callUserInfo, dragRef, onFixed, setVolume]
  );

  useEffect(() => {
    const { updateUserStreams } = refs.current;
    if (callUserInfo?.updateStreams) {
      if (callUserInfo?.callActionMap.screenSharing_video && member?.screenSharing) {
        updateUserStreams({
          user: { id: callUserInfo?.id, updateStreams: callUserInfo?.updateStreams },
          mediaDoms: { screenVideoRef: videoRef },
        });
      } else {
        updateUserStreams({
          user: { id: callUserInfo?.id, updateStreams: callUserInfo?.updateStreams },
          mediaDoms: { videoRef },
        });
      }
    }
  }, [
    refs,
    callUserInfo?.id,
    callUserInfo?.updateStreams,
    callUserInfo?.callActionMap?.screenSharing_video,
    callUserInfo?.callActionMap?.microphoneCamera_video,
    callUserStream?.updateStreams,
    member?.screenSharing,
  ]);

  return (
    <div
      style={style}
      ref={dragRef}
      className={cls(
        styles.callCard,
        isSelfUnFixed && !isOnlyOne && styles.floatCallCard,
        isSelfUnFixed && !isOnlyOne && ringing && styles.hidden,
        mediaModalZoomIn
          ? styles.zoomIn
          : isSelfUnFixed && !isOnlyOne
            ? styles.selfZoomOut
            : styles.zoomOut,
        !ringing && styles.zoomIn
      )}
    >
      <video
        className={cls(
          styles.video,
          !member?.screenSharing &&
            !callUserInfo?.callActionMap.microphoneCamera_video &&
            styles.closeVideo
        )}
        // hidden={callUserInfo?.callActionMap.screenSharing_video}
        playsInline
        autoPlay
        ref={videoRef}
      ></video>
      {/* <video
        className={cls(styles.video)}
        hidden={!callUserInfo?.callActionMap.screenSharing_video}
        playsInline
        autoPlay
        ref={screenVideoRef}
      ></video> */}
      {/* <audio ref={audioRef}></audio> */}
      {!member?.screenSharing &&
        !callUserInfo?.callActionMap.microphoneCamera_video && (
          <div className={styles.avatarBox}>
            <img
              className={cls(
                mediaModalZoomIn
                  ? isSelfUnFixed && !isOnlyOne
                    ? styles.avatar
                    : styles.avatarZoomIn
                  : isSelfUnFixed && !isOnlyOne
                    ? styles.avatarZoomOut
                    : styles.avatar
              )}
              src={member.avatar}
            />
            {mediaIsPutThrough && !mediaIsReady && (
              <div
                className={cls(
                  styles.tipsBar,
                  styles.connecting,
                  !isSelfUnFixed && !mediaModalZoomIn && styles.show
                )}
              >
                <CallIcon />
                <div className={styles.textBar}>Connecting...</div>
              </div>
            )}
            {mediaIsPutThrough && mediaIsReady && (
              <div
                className={cls(
                  styles.tipsBar,
                  styles.mediaIsPutThrough,
                  !isSelfUnFixed && !mediaModalZoomIn && styles.show
                )}
              >
                {callUserInfo?.callActionMap.microphoneCamera_audio &&
                !mediaModalZoomIn ? (
                  <DrawSpeak
                    callUserInfo={callUserInfo}
                    callUserStream={callUserStream}
                    member={member}
                  ></DrawSpeak>
                ) : (
                  <UnMuteIcon />
                )}
                <CallTimer startTime={mediaStartTime} />
              </div>
            )}
            {ringing && (
              <div className={styles.statusBar}>
                <div className={styles.name}>{member.name}</div>
                {ringing &&
                  chatMediaCall.chatMediaInfo?.caller?.userId ===
                    session.user?.userId && (
                    <div className={styles.tipsBar}>
                      <CallIcon />
                      <div className={styles.textBar}>Ringing...</div>
                    </div>
                  )}
              </div>
            )}
          </div>
        )}

      {mediaModalZoomIn && (
        <>
          {member.fixed && (
            <div className={styles.fixed}>
              <FixedIcon />
            </div>
          )}

          {mediaIsPutThrough && (
            <div className={styles.operate}>
              <div className={styles.info}>
                {member.screenSharing ? (
                  <ScreenIcon />
                ) : callUserInfo?.callActionMap.microphoneCamera_audio ? (
                  <DrawSpeak
                    callUserInfo={callUserInfo}
                    callUserStream={callUserStream}
                    member={member}
                  ></DrawSpeak>
                ) : (
                  <UnMuteIcon />
                )}
                <div>
                  {member.userId === user.userId ? 'Me' : member.name}
                  {/* {member?.status === CallMemberStatus.TALKING && '(Calling)'} */}
                </div>
              </div>
              {!isSelfUnFixed && (
                <CardMoreOperate className={styles.more} onClick={handleClickMore}>
                  <MoreIcon />
                </CardMoreOperate>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default CallCard;
